import React, { useState } from "react";
import { Link } from "react-router-dom";
import { QtyStepper, WeightNote } from "@/components/QtyStepper";
import { SupplierImg } from "@/components/SupplierImg";
import { unitWeightKg } from "@/lib/weight";
import { Heart, MapPin, Truck, ShoppingCart, Zap, Scale, Flame, AlertTriangle, Clock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCompare } from "@/context/CompareContext";
import { useDeal } from "@/context/DealContext";
import { Stars } from "@/components/Stars";
import { jmd } from "@/lib/format";
import { Price } from "@/components/Price";
import { assetUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export function ProductCard({ product }) {
  const { addToCart, toggleWish, inWishlist } = useCart();
  const { toggleCompare, inCompare } = useCompare();
  const deal = useDeal();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const unitKg = unitWeightKg(product);
  const wished = inWishlist(product.id);
  const comparing = inCompare(product.id);
  const isDeal = deal.dealId === product.id;
  const dealRemaining = isDeal ? Math.max(0, (deal.allocation || 0) - (deal.claimed || 0)) : 0;
  const dealSoldOut = isDeal && deal.allocation > 0 && dealRemaining <= 0;
  const almostGone = isDeal && deal.allocation > 0 && dealRemaining > 0 && dealRemaining <= deal.allocation * 0.2;
  const isDealSold = isDeal && deal.claimed > 0 && !almostGone && !dealSoldOut;
  const lowStock = !product.price_on_request && product.stock > 0 && product.stock <= 8;

  return (
    <div data-testid="product-card"
      className="group relative flex flex-col bg-card rounded-2xl border border-border overflow-hidden sn-card-hover">
      <Link to={`/product/${product.id}`} className="relative block aspect-square overflow-hidden bg-slate-100">
        <SupplierImg product={product} alt={product.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {product.image_placeholder && (
            <span data-testid="image-pending-badge" className="bg-black/70 text-white text-[10px] font-bold px-2.5 py-1 sn-pill uppercase tracking-wider">Image pending · category photo</span>
          )}
          {product.is_new && (
            <span data-testid="new-badge" className="bg-emerald text-white text-[10px] font-extrabold px-2.5 py-1 sn-pill font-display uppercase tracking-[0.12em] shadow-lg">
              New
            </span>
          )}
          {product.discount > 0 && (
            <span className="bg-ink text-white text-xs font-bold px-2.5 py-1 sn-pill font-display">
              -{product.discount}%
            </span>
          )}
          {isDealSold && (
            <span data-testid="deal-sold-badge" className="flex items-center gap-1 bg-coral text-white text-[10px] font-extrabold px-2 py-1 sn-pill font-display uppercase tracking-wide shadow-lg animate-pulse">
              <Flame size={11} /> {deal.claimed} sold today
            </span>
          )}
          {almostGone && (
            <span data-testid="deal-almost-gone-badge" className="flex items-center gap-1 bg-coral text-white text-[10px] font-extrabold px-2 py-1 sn-pill font-display uppercase tracking-wide shadow-lg animate-pulse">
              <Clock size={11} /> Almost gone — {dealRemaining} left
            </span>
          )}
          {dealSoldOut && (
            <span data-testid="deal-soldout-badge" className="flex items-center gap-1 bg-ink text-white text-[10px] font-extrabold px-2 py-1 sn-pill font-display uppercase tracking-wide shadow-lg">
              Sold out today
            </span>
          )}
          {lowStock && (
            <span data-testid="low-stock-badge" className="flex items-center gap-1 bg-amber-500 text-white text-[10px] font-extrabold px-2 py-1 sn-pill font-display uppercase tracking-wide shadow-lg">
              <AlertTriangle size={11} /> Only {product.stock} left
            </span>
          )}
        </div>
        <button data-testid="wishlist-btn"
          onClick={(e) => { e.preventDefault(); toggleWish(product); }}
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/90 backdrop-blur hover:bg-white transition-colors">
          <Heart size={17} className={wished ? "fill-coral text-coral" : "text-ink"} />
        </button>
        <button data-testid="compare-btn"
          onClick={(e) => { e.preventDefault(); toggleCompare(product); }}
          title="Compare"
          className={`absolute top-14 right-3 h-9 w-9 grid place-items-center rounded-full backdrop-blur transition-colors ${comparing ? "bg-ink text-white" : "bg-white/90 hover:bg-white text-ink"}`}>
          <Scale size={16} />
        </button>
      </Link>

      <div className="flex flex-col flex-1 p-4">
        <div className="flex items-center gap-1.5 text-[11px] text-emerald font-semibold uppercase tracking-wide mb-1">
          <MapPin size={12} /> {product.parish}
        </div>
        <Link to={`/product/${product.id}`}
          className="font-display font-semibold text-[15px] leading-snug line-clamp-2 hover:text-emerald transition-colors">
          {product.name}
        </Link>
        <Link to={`/merchant/${product.merchant_slug}`} className="text-xs text-muted-foreground mt-1 hover:text-emerald">
          {product.brand}
        </Link>
        {product.sku && <div className="text-[10px] text-muted-foreground/70 font-mono-sn mt-0.5 uppercase tracking-wide">Code {product.sku}</div>}
        <div className="flex items-center gap-2 mt-2">
          <Stars rating={product.rating} />
          <span className="text-xs text-muted-foreground">({product.reviews})</span>
        </div>
        <div className="flex items-end gap-2 mt-3">
          {product.price_on_request ? (
            <span className="font-display font-bold text-[15px] text-coral">Request Current Price</span>
          ) : product.availability_status === "availability_unknown" ? (
            <span className="font-display font-extrabold text-lg text-ink"><Price amount={product.price} /> <span className="text-[10px] font-bold uppercase text-amber-700 ml-1">availability unknown</span></span>
          ) : (
            <>
              <Price amount={product.price} testid={`price-${product.id}`} />
              {product.prev_price > product.price && (
                <Price amount={product.prev_price} size="sm" strike />
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
          <Truck size={12} /> {product.delivery_days}-day delivery
        </div>

        <div className="mt-auto pt-3 grid grid-cols-2 gap-2">
          {product.price_on_request ? (
            <>
              <div className="col-span-2 flex items-center justify-between gap-2 flex-wrap"><QtyStepper value={qty} onChange={setQty} size="sm" id={`card-qty-${product.id}`} unit={product.unit} /><WeightNote unitKg={unitKg} qty={qty} unit={product.unit} id={`card-weight-${product.id}`} compact /></div>
              <Link to={`/product/${product.id}?qty=${qty || 1}`} data-testid="enquire-btn" aria-label={`Order ${product.name} — request quotation`}
                className="col-span-2 flex items-center justify-center gap-1.5 rounded-xl bg-coral text-white py-2 text-sm font-bold hover:bg-[#d62f26] transition-colors">
                <ShoppingCart size={15} /> Order · Request quotation
              </Link>
            </>
          ) : (
            <>
              <div className="col-span-2 flex items-center justify-between gap-2 flex-wrap"><QtyStepper value={qty} onChange={setQty} size="sm" id={`card-qty-${product.id}`} unit={product.unit} /><WeightNote unitKg={unitKg} qty={qty} unit={product.unit} id={`card-weight-${product.id}`} compact /></div>
              <button data-testid="add-to-cart-btn" disabled={qty < 1} onClick={() => { addToCart(product, qty); setQty(1); }} aria-label={`Order ${product.name}, quantity ${qty}`}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-coral text-white py-2 text-sm font-bold hover:bg-[#d62f26] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title={qty < 1 ? "Select at least 1" : ""}>
                <ShoppingCart size={15} /> Order{qty > 0 ? ` ${qty}` : ""}
              </button>
              <button data-testid="buy-now-btn" disabled={qty < 1}
                onClick={() => { addToCart(product, qty, false); navigate("/checkout"); }}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-ink text-white py-2 text-sm font-semibold hover:bg-emerald-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <Zap size={15} /> Buy
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ProductRail({ title, products, action }) {
  if (!products?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-5">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
        {action}
      </div>
      <div className="flex gap-4 overflow-x-auto sn-scroll-x pb-2 -mx-1 px-1">
        {products.map((p) => (
          <div key={p.id} className="min-w-[240px] max-w-[240px]"><ProductCard product={p} /></div>
        ))}
      </div>
    </section>
  );
}
